import React from 'react';
import { X, Minus, Plus, Trash2, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CartItem, Page } from '../App';
interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
  onNavigate: (page: Page) => void;
}
export function CartSidebar({
  isOpen,
  onClose,
  items,
  onUpdateQuantity,
  onRemoveItem,
  onNavigate
}: CartSidebarProps) {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0
  }).format(amount);
  return <AnimatePresence data-id="element-7">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" data-id="element-8" />
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed top-0 right-0 h-full w-full max-w-md bg-[#141414] border-l border-white/10 z-[70] shadow-2xl flex flex-col" data-id="element-9">
            <div className="p-6 border-b border-white/10 flex items-center justify-between" data-id="element-10">
              <h2 className="text-xl font-serif font-bold text-white" data-id="element-11">
                Your Cart ({items.length})
              </h2>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-full transition-colors" data-id="element-12">
                <X className="w-5 h-5" data-id="element-13" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-14">
              {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-4" data-id="element-15">
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center text-gray-500" data-id="element-16">
                    <Trash2 className="w-8 h-8" data-id="element-17" />
                  </div>
                  <p className="text-gray-400" data-id="element-18">Your cart is empty</p>
                  <button onClick={onClose} className="text-[#D4AF37] hover:underline font-medium" data-id="element-19">
                    Start Shopping
                  </button>
                </div> : items.map(item => <div key={item.id} className="flex gap-4" data-id="element-20">
                    <div className="w-20 h-20 rounded-lg bg-white/5 overflow-hidden flex-shrink-0" data-id="element-21">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-22" />
                    </div>
                    <div className="flex-1 flex flex-col justify-between" data-id="element-23">
                      <div data-id="element-24">
                        <h3 className="text-white font-medium line-clamp-1" data-id="element-25">
                          {item.name}
                        </h3>
                        <p className="text-[#D4AF37] font-medium mt-1" data-id="element-26">
                          {formatCurrency(item.price)}
                        </p>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-27">
                        <div className="flex items-center space-x-3 bg-white/5 rounded-lg p-1" data-id="element-28">
                          <button onClick={() => onUpdateQuantity(item.id, -1)} className="p-1 text-gray-400 hover:text-white disabled:opacity-50" disabled={item.quantity <= 1} data-id="element-29">
                            <Minus className="w-3 h-3" data-id="element-30" />
                          </button>
                          <span className="text-sm text-white font-medium w-4 text-center" data-id="element-31">
                            {item.quantity}
                          </span>
                          <button onClick={() => onUpdateQuantity(item.id, 1)} className="p-1 text-gray-400 hover:text-white" data-id="element-32">
                            <Plus className="w-3 h-3" data-id="element-33" />
                          </button>
                        </div>
                        <button onClick={() => onRemoveItem(item.id)} className="text-gray-500 hover:text-red-400 transition-colors text-sm" data-id="element-34">
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>)}
            </div>

            {items.length > 0 && <div className="p-6 border-t border-white/10 bg-[#1a1a1a]" data-id="element-35">
                <div className="space-y-3 mb-6" data-id="element-36">
                  <div className="flex justify-between text-gray-400" data-id="element-37">
                    <span data-id="element-38">Subtotal</span>
                    <span data-id="element-39">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400" data-id="element-40">
                    <span data-id="element-41">Shipping</span>
                    <span className="text-[#D4AF37]" data-id="element-42">
                      Calculated at checkout
                    </span>
                  </div>
                  <div className="flex justify-between text-white font-bold text-lg pt-3 border-t border-white/10" data-id="element-43">
                    <span data-id="element-44">Total</span>
                    <span className="text-[#D4AF37]" data-id="element-45">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>
                </div>
                <button onClick={() => {
            onClose();
            onNavigate('checkout');
          }} className="w-full bg-[#D4AF37] text-black font-bold py-4 rounded-lg hover:bg-[#c4a030] transition-colors flex items-center justify-center gap-2" data-id="element-46">
                  Checkout Securely
                  <ArrowRight className="w-4 h-4" data-id="element-47" />
                </button>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}